import {
  loginWithPhone,
  otpLogin,
  registerCustomer,
  resetPassword,
  sendOtp,
  validateToken,
  verifyAndResetPassword,
} from "@/requests/auth";
import { removeTokenCookie, setTokenCookie } from "@/utils";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { asyncThunkHandler } from "../reducers";
import { sendResetEmail } from "./../../requests/auth";

interface AuthState {
  user: User | null;
  access_token: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  access_token: null,
  isLoading: true,
  error: null,
};

// ** auth reducers
const authPending = (state: AuthState) => {
  state.isLoading = true;
  state.error = null;
};

const authFulfilled = (
  state: AuthState,
  action: PayloadAction<AuthResponse>
) => {
  state.isLoading = false;
  state.user = action.payload.user;
  state.access_token = action.payload.access_token;

  setTokenCookie(action.payload.access_token);
};

const authRejected = (state: AuthState, action: any) => {
  state.isLoading = false;
  state.error = action.payload;
  state.user = null;
  state.access_token = null;

  removeTokenCookie();
};

// ** auth thunks
// validate token async thunk
export const validateTokenAsync = createAsyncThunk(
  "auth/validateToken",
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await validateToken(token);
      // console.log({ response });
      
      return {
        user: response,
        access_token: token,
      } as AuthResponse;
    } catch (error) {
      return rejectWithValue(error as any);
    }
  }
);
// login async thunk
export const loginAsync = asyncThunkHandler<LoginRequest, AuthResponse>(
  "auth/login",
  loginWithPhone
);
// register async thunk
export const registerAsync = asyncThunkHandler<RegesterRequest, AuthResponse>(
  "auth/register",
  registerCustomer
);
// send otp async thunk
export const sendOtpAsync = asyncThunkHandler<SendOtpRequest, SendOtpResponse>(
  "auth/sendOtp",
  sendOtp
);
// otp login async thunk
export const otpLoginAsync = asyncThunkHandler<OtpLoginRequest, AuthResponse>(
  "auth/otpLogin",
  otpLogin
);
// reset password async thunk
export const resetPasswordAsync = asyncThunkHandler<ResetPasswordRequest, any>(
  "auth/resetPassword",
  resetPassword
);
export const resetPasswordEmailAsync = asyncThunkHandler<any, any>(
  "auth/resetPasswordEmail",
  sendResetEmail
);
export const varifyAndChangePasswordAsync = asyncThunkHandler<any, any>(
  "auth/changePasswordEmail",
  verifyAndResetPassword
);

// ** auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // auth login start reducer
    loginStart: authPending,
    // auth login success reducer
    loginSuccess: authFulfilled,
    // auth login failure reducer
    loginFailure: authRejected,
    // auth logout reducer
    logout: (state) => {
      state.user = null;
      state.access_token = null;
      state.error = null;
      state.isLoading = false;

      removeTokenCookie();
    },
    setErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    // * validate token actions
    builder.addCase(validateTokenAsync.pending, authPending);

    builder.addCase(validateTokenAsync.fulfilled, authFulfilled);

    builder.addCase(validateTokenAsync.rejected, authRejected);

    // * login actions
    builder.addCase(loginAsync.pending, authPending);

    builder.addCase(loginAsync.fulfilled, authFulfilled);

    builder.addCase(loginAsync.rejected, authRejected);

    // * register actions
    builder.addCase(registerAsync.pending, authPending);

    builder.addCase(registerAsync.fulfilled, authFulfilled);

    builder.addCase(registerAsync.rejected, authRejected);

    // * otp login actions
    builder.addCase(otpLoginAsync.pending, authPending);

    builder.addCase(otpLoginAsync.fulfilled, authFulfilled);

    builder.addCase(otpLoginAsync.rejected, authRejected);

    // * reset with email
    builder.addCase(resetPasswordEmailAsync.pending, authPending);

    builder.addCase(resetPasswordEmailAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });

    builder.addCase(resetPasswordEmailAsync.rejected, authRejected);

    // * varify and change password
    builder.addCase(varifyAndChangePasswordAsync.pending, authPending);

    builder.addCase(varifyAndChangePasswordAsync.fulfilled, (state, action) => {
      state.isLoading = false;
      state.error = null;
    });

    builder.addCase(varifyAndChangePasswordAsync.rejected, authRejected);
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  setErrorMessage,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
